import React from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import {
  SelectElement,
  TextFieldElement,
  TextareaAutosizeElement,
  useForm,
} from "react-hook-form-mui";
import { z } from "zod";

import { zodResolver } from "@hookform/resolvers/zod";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation } from "aws-amplify";
import { getLLMGroup } from "../graphql/queries";
import { GetLLMGroupQuery } from "../API";
import { GraphQLResult } from "@aws-amplify/api";
import { useUser } from "../contexts/useUser";
import { updateLLMGroupAfterEdit } from "../dataaccess/updateGroupAfterEdit";
import { createLLMGroupAfterEdit } from "../dataaccess/createLLMGroupAfterEdit";

const initialValues = {
  name: "",
  status: "active",
  description: "",
  systemInstructions: "",
  systemInstructionsAction: "append",
  order: 0,
};

const schema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, "Name is required"),
  status: z.string().min(1, "Status is required"),
  description: z.string().optional().nullable(),
  systemInstructions: z.string().optional().nullable(),
  systemInstructionsAction: z.string().optional().nullable(),
  order: z.number().optional().nullable(),
});

type GroupFormType = z.infer<typeof schema>;

const GroupForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { logError } = useErrorLogging();
  const { project } = useUser();

  const queryClient = useQueryClient();

  const mutationUpdateGroup = useMutation({
    mutationFn: updateLLMGroupAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });

      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error updating group", error as any);
    },
  });

  const mutationCreateGroup = useMutation({
    mutationFn: createLLMGroupAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });

      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error creating group", error as any);
    },
  });

  const {
    data: groupData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["groups", { id: id }],
    queryFn: () =>
      API.graphql(graphqlOperation(getLLMGroup, { id: id })) as Promise<
        GraphQLResult<GetLLMGroupQuery>
      >,
    enabled: !!id,
  });

  const { handleSubmit, control, reset } = useForm<GroupFormType>({
    values: groupData?.data?.getLLMGroup || initialValues,
    resolver: zodResolver(schema),
  });

  if (id && isLoading) return <div>Loading...</div>;
  if (id && isError) return <div>Error loading project</div>;

  const handleUpdate = (values: GroupFormType) => {
    if (!values) return;

    try {
      mutationUpdateGroup.mutate({ id: id, ...values } as any);
    } catch (error) {
      logError("Error updating group", error as any);
    }
  };

  const handleCreate = (values: GroupFormType) => {
    if (!values) return;
    if (!project) return;

    mutationCreateGroup.mutate({
      projectId: project?.id,
      ...values,
    });
  };

  const handleClose = () => {
    window.history && window.history.length > 1 ? navigate(-1) : navigate("/");
  };

  const onSubmit = (data: GroupFormType) => {
    if (id) handleUpdate(data);
    else handleCreate(data);
  };

  return (
    <Paper sx={{ margin: 2, padding: 2, borderRadius: 3, border: "1px solid #444" }}>
      <Typography padding={2} variant="h6">
        Group Settings
      </Typography>
      <Grid container padding={2} spacing={2}>
        <Grid item xs={12} md={6}>
          <TextFieldElement
            control={control}
            name="name"
            label="Name"
            size="small"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="status"
            label="Group status"
            size="small"
            required
            fullWidth
            options={[
              { id: "active", label: "Active" },
              { id: "disabled", label: "Disabled" },
              { id: "deleted", label: "Deleted" },
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          <TextFieldElement
            control={control}
            name="description"
            label="Description"
            size="small"
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <TextareaAutosizeElement
            control={control}
            name="systemInstructions"
            label="Group System Instructions Append or Replace (use to always include brand and policy information to prompts sent to LLMs)"
            size="small"
            fullWidth
            rows={3}
          />
        </Grid>
        <Grid item xs={12}>
          <SelectElement
            control={control}
            name="systemInstructionsAction"
            label="System Instructions Action"
            size="small"
            required
            fullWidth
            options={[
              { id: "append", label: "Append to project instructions" },
              { id: "replace", label: "Replace project instructions" },
            ]}
          />
        </Grid>
      </Grid>
      <Box sx={{ padding: 2 }}>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant={"contained"}
          sx={{ marginRight: 2 }}
        >
          Save and Close
        </Button>
        <Button onClick={() => reset()} variant={"outlined"}>
          Reset
        </Button>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
      </Box>
    </Paper>
  );
};

export default GroupForm;
